/** Typed event bus. Systems emit; UI, audio, missions and analytics subscribe. */
export interface GameEvents {
  /** A new trip: 1 is the first, then after every U-turn at a terminus. */
  'trip:started': { trip: number; from: string; to: string };
  'leg:entered': { legId: string; name: string; subtitle: string };
  'stop:approaching': { stopName: string; distance: number };
  'stop:rated': { stopName: string; rating: 'perfect' | 'good' | 'overshot' };
  'passenger:boarded': { stopName: string; onBoard: number };
  'passenger:dropped': { stopName: string; farePesewas: number; tipPesewas: number };
  'passenger:missed': { stopName: string; count: number };
  'run:closeCall': { kind: string };
  'run:horn': { cleared: number };
  'pickup:coin': { total: number };
  'pickup:star': { factor: number; seconds: number };
  'pickup:gift': { kind: 'cash' | 'fitter'; coins: number; repaired: number };
  'powerUp:started': { id: string; name: string; seconds: number };
  'powerUp:ended': { id: string };
  /** The shield took a crash, or the siren blasted a car out of the way. */
  'powerUp:saved': { id: 'shield' | 'siren'; cause: string };
  'junction:approaching': { name: string; signal: 'green' | 'amber' | 'red' };
  'junction:ranRed': { name: string; finePesewas: number };
  /** A scrape: small repair, a passenger jumps off. */
  'run:stumbled': { cause: string; repairPesewas: number };
  /** A real crash: the trotro stops, pays repairs (or a fine for hitting someone from behind) and carries on. */
  'run:collision': { cause: string; rearEnd: boolean; repairPesewas: number; finePesewas: number; damage: number };
  /** Too much damage: the trotro breaks down and the shift ends. */
  'run:brokeDown': { legName: string };
  'junction:pedestrianFine': { name: string; finePesewas: number };
  /** Fuel: running low (with the next station), the tank is empty, filling at a pump, tank full. */
  'fuel:low': { level: 1 | 2 | 3; units: number; station: string | null; distance: number };
  'fuel:empty': Record<string, never>;
  'fuel:filling': { station: string };
  'fuel:full': { station: string };
  /** Police checkpoint: one is ahead, you drove past it, or you were checked (papers fine or not). */
  /** The trotro starts its U-turn at the end of the road, and has come out on the other carriageway. */
  'uturn:started': Record<string, never>;
  'uturn:done': Record<string, never>;
  /** A one-way journey reached its end. */
  'journey:arrived': Record<string, never>;
  'checkpoint:ahead': { distance: number };
  'checkpoint:ran': { finePesewas: number };
  'checkpoint:cleared': { papersProblem: boolean; finePesewas: number; dashed: boolean };
  /** The officer asks for a dash; you gave one (amount in pesewas); you said no. */
  'checkpoint:dashAsk': Record<string, never>;
  'checkpoint:dash': { amountPesewas: number };
  'checkpoint:dashRefused': Record<string, never>;
  'mate:say': { text: string };
}

type Handler<T> = (payload: T) => void;

export class EventBus {
  private readonly handlers = new Map<keyof GameEvents, Set<Handler<never>>>();

  on<K extends keyof GameEvents>(type: K, handler: Handler<GameEvents[K]>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler as Handler<never>);
    return () => set.delete(handler as Handler<never>);
  }

  emit<K extends keyof GameEvents>(type: K, payload: GameEvents[K]): void {
    const set = this.handlers.get(type);
    if (!set) return;
    for (const handler of set) (handler as Handler<GameEvents[K]>)(payload);
  }

  clear(): void {
    this.handlers.clear();
  }
}
